import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Brain, TrendingUp, Shield, Zap, AlertTriangle } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export default function AIShowcase() {
  const navigate = useNavigate();
  const [riskFilter, setRiskFilter] = useState<string>("all");

  const { data: deals = [], isLoading } = useQuery({
    queryKey: ["ai-analyzed-deals"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deals")
        .select(`
          *,
          athletes (name, sport)
        `)
        .not("ai_risk_score", "is", null)
        .order("ai_analyzed_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const filteredDeals = deals.filter((deal) => {
    if (riskFilter === "all") return true;
    return deal.ai_risk_level === riskFilter;
  });

  const stats = {
    total: deals.length,
    low: deals.filter((d) => d.ai_risk_level === "low").length,
    medium: deals.filter((d) => d.ai_risk_level === "medium").length,
    high: deals.filter((d) => d.ai_risk_level === "high").length,
    critical: deals.filter((d) => d.ai_risk_level === "critical").length,
    avgScore: Math.round(deals.reduce((sum, d) => sum + (d.ai_risk_score || 0), 0) / deals.length) || 0,
  };

  const getRiskColor = (level: string) => {
    switch (level) {
      case "low": return "default";
      case "medium": return "secondary";
      case "high": return "destructive";
      case "critical": return "destructive";
      default: return "outline";
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Brain className="h-8 w-8 text-primary" />
            AI-Powered Deal Analysis
          </h1>
          <p className="text-muted-foreground mt-2">
            Comprehensive AI risk assessment across all NIL deals
          </p>
        </div>

        {/* AI Performance Stats */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Analyzed</CardTitle>
              <Zap className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-muted-foreground">AI-powered reviews</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Violations Prevented</CardTitle>
              <Shield className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.high + stats.critical}</div>
              <p className="text-xs text-muted-foreground">High-risk deals blocked</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Average Risk Score</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.avgScore}/100</div>
              <p className="text-xs text-muted-foreground">Portfolio health</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Review Speed</CardTitle>
              <Zap className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">&lt;2s</div>
              <p className="text-xs text-muted-foreground">vs 2-3 days manual</p>
            </CardContent>
          </Card>
        </div>

        {/* Risk Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Risk Distribution</CardTitle>
            <CardDescription>Breakdown of AI risk assessments across all deals</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{stats.low}</div>
                <div className="text-sm text-muted-foreground">Low Risk</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-600">{stats.medium}</div>
                <div className="text-sm text-muted-foreground">Medium Risk</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-600">{stats.high}</div>
                <div className="text-sm text-muted-foreground">High Risk</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600">{stats.critical}</div>
                <div className="text-sm text-muted-foreground">Critical Risk</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filter Controls */}
        <div className="flex items-center gap-4">
          <Select value={riskFilter} onValueChange={setRiskFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by risk" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Risk Levels</SelectItem>
              <SelectItem value="low">Low Risk</SelectItem>
              <SelectItem value="medium">Medium Risk</SelectItem>
              <SelectItem value="high">High Risk</SelectItem>
              <SelectItem value="critical">Critical Risk</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Deal Cards */}
        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground">Loading AI analyses...</div>
        ) : filteredDeals.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No deals found with AI analysis</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredDeals.map((deal) => (
              <Card
                key={deal.id}
                className="cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate(`/dashboard/deals/${deal.id}`)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{deal.title}</CardTitle>
                      <CardDescription>{deal.athletes?.name} • {deal.athletes?.sport}</CardDescription>
                    </div>
                    <Badge variant={getRiskColor(deal.ai_risk_level || "")}>
                      {deal.ai_risk_level}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Risk Score</span>
                    <span className="text-2xl font-bold">{deal.ai_risk_score}/100</span>
                  </div>
                  
                  {deal.ai_concerns && Array.isArray(deal.ai_concerns) && deal.ai_concerns.length > 0 && (
                    <div className="space-y-1">
                      <span className="text-sm font-medium">Key Concerns:</span>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        {deal.ai_concerns.slice(0, 2).map((concern: string, idx: number) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-destructive">•</span>
                            <span className="flex-1">{concern}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="pt-2 border-t">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Deal Value</span>
                      <span className="font-semibold">${deal.amount.toLocaleString()}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
